import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import { CountryCombobox } from '@/components/ui/country-combobox'
import { Combobox } from '@/components/ui/combobox'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useStates, useCities } from '@/hooks'
import { FormModeEnum } from '@/enums'
import { AddressType, CreateAddressParams, UpdateAddressParams } from '@/types/address'

export type AddressFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: FormModeEnum
  initialData?: AddressType | null
  isLoading?: boolean
  onSubmit: (data: CreateAddressParams | UpdateAddressParams) => void
}

const baseAddressFormSchema = (t: ReturnType<typeof useTranslations>) =>
  z.object({
    title: z
      .string()
      .trim()
      .min(1, t('not_blank'))
      .max(100, t('max_value', { value: 100 })),
    countryId: z.string().min(1, t('not_blank')),
    stateId: z.string().min(1, t('not_blank')),
    cityId: z.string(),
    zipCode: z
      .string()
      .min(1, t('not_blank'))
      .max(12, t('max_value', { value: 12 })),
    text: z.string().min(1, t('not_blank')),
    primary: z.boolean()
  })

type AddressFormData = z.infer<ReturnType<typeof baseAddressFormSchema>>

const getDefaultValues = (initialData?: AddressType | null): AddressFormData => ({
  title: initialData?.title || '',
  countryId: initialData?.country?.id || '',
  stateId: initialData?.state?.id || '',
  cityId: initialData?.city?.id || '',
  zipCode: initialData?.zipCode || '',
  text: initialData?.text || '',
  primary: initialData?.primary ?? false
})

export const AddressFormDialog = ({
  open,
  onOpenChange,
  mode,
  initialData,
  isLoading = false,
  onSubmit
}: AddressFormDialogProps) => {
  const t = useTranslations()

  const citiesRef = useRef<{ length: number }>({ length: 0 })
  const isLoadingCitiesRef = useRef(false)

  const schema = useMemo(
    () =>
      baseAddressFormSchema(t).superRefine((data, ctx) => {
        if (!isLoadingCitiesRef.current && citiesRef.current.length > 0 && !data.cityId) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['cityId'] })
        }
      }),
    [t]
  )

  const form = useForm<AddressFormData>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(initialData)
  })

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues(initialData))
    }
  }, [form, initialData, open])

  const countryId = form.watch('countryId')
  const stateId = form.watch('stateId')

  const { data: states = [], isLoading: isLoadingStates } = useStates(countryId || undefined)
  const { data: cities = [], isLoading: isLoadingCities } = useCities(stateId || undefined)

  useEffect(() => {
    citiesRef.current = cities
    isLoadingCitiesRef.current = isLoadingCities
  }, [cities, isLoadingCities])

  const stateOptions = states.map(s => ({
    label: `${s.name} (${s.code})`,
    value: s.id,
    searchValue: `${s.name} ${s.code}`.toLowerCase()
  }))
  const cityOptions = cities.map(c => ({ label: c.name, value: c.id, searchValue: c.name.toLowerCase() }))

  const handleSubmit = (data: AddressFormData) => {
    onSubmit({
      title: data.title.trim(),
      countryId: data.countryId || undefined,
      stateId: data.stateId || undefined,
      cityId: data.cityId || undefined,
      zipCode: data.zipCode.trim(),
      text: data.text.trim(),
      primary: data.primary
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-6 sm:max-w-2xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t(mode === FormModeEnum.CREATE ? 'add_address' : 'edit_address')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-1">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel required>{t('address_title')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={100}
                      autoFocus
                      disabled={isLoading}
                      placeholder={t('enter_address_title')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-x-2 gap-y-1 md:grid-cols-3">
              <FormField
                control={form.control}
                name="countryId"
                render={({ field, fieldState }) => (
                  <FormItem className="min-w-0 gap-1">
                    <FormLabel required>{t('country')}</FormLabel>
                    <FormControl>
                      <CountryCombobox
                        value={field.value || ''}
                        onValueChange={value => {
                          field.onChange(value)
                          form.setValue('stateId', '')
                          form.setValue('cityId', '')
                        }}
                        placeholder={t('select')}
                        disabled={isLoading}
                        error={!!fieldState.error}
                        valueField="id"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="stateId"
                render={({ field, fieldState }) => (
                  <FormItem className="min-w-0 gap-1">
                    <FormLabel required>{t('state')}</FormLabel>
                    <FormControl>
                      <Combobox
                        options={stateOptions}
                        value={field.value || ''}
                        onValueChange={value => {
                          field.onChange(value)
                          form.setValue('cityId', '')
                        }}
                        placeholder={t('select')}
                        searchPlaceholder={t('search')}
                        emptyText={isLoadingStates ? t('loading') : t('not_found')}
                        disabled={isLoading || isLoadingStates || !countryId}
                        clearable
                        error={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cityId"
                render={({ field, fieldState }) => (
                  <FormItem className="min-w-0 gap-1">
                    <FormLabel required={!isLoadingCities && cities.length > 0}>{t('city')}</FormLabel>
                    <FormControl>
                      <Combobox
                        options={cityOptions}
                        value={field.value || ''}
                        onValueChange={field.onChange}
                        placeholder={t('select')}
                        searchPlaceholder={t('search')}
                        emptyText={isLoadingCities ? t('loading') : t('not_found')}
                        disabled={isLoading || isLoadingCities || !stateId}
                        clearable
                        error={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem className="gap-1">
                    <FormLabel required>{t('zip_code')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        maxLength={12}
                        disabled={isLoading}
                        placeholder={t('enter_zip_code')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="text"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel required>{t('address_text')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ''}
                      rows={3}
                      disabled={isLoading}
                      placeholder={t('enter_address_text')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="primary"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-3">
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isLoading} />
                  </FormControl>
                  <FormLabel className="mt-0!">{t('primary_address')}</FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="mt-5">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? t('saving') : t(mode)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default AddressFormDialog
